import React, { useCallback, useEffect, useState } from 'react'
import { Box, Grid, Typography, Button, Tooltip } from '@material-ui/core'
import { FileRejection, useDropzone } from 'react-dropzone'
import { LogoUploadContainer } from 'screens/AccountSettings/containers/Company/Company.style'
import useIntl from 'hooks/useIntl'
import iziToast from 'izitoast'
import axios from 'axios'
import Bulksections from './Bulksections'
import Bulksubsection from './Bulksubsection'
import { useStoreon } from 'storeon/react'

const FileUpload = ({ handleClose, refreshData }) => {
	const [file, setFile] = useState<File | null>(null)
	const [loading, setLoading] = useState(false)

	const [sectionData, setSectionData] = useState<any[]>([])
	const [subSectionData, setSubSectionData] = useState<any[]>([])
	const [selectedSection, setSelectedSection] = useState<string>('')
	const [selectedSubSection, setSelectedSubSection] = useState<string>('')

	const [isCustomSection, setIsCustomSection] = useState(false)
	const [customSection, setCustomSection] = useState('')

	const [isCustomSubSection, setIsCustomSubSection] = useState(false)
	const [customSubSection, setCustomSubSection] = useState('')

	const intl = useIntl()
	const { token } = useStoreon('token')
	const handleUpload = async () => {
		if (!file) {
			iziToast.error({ message: 'No file selected. Please upload a file first.' })
			return
		}

		if (!selectedSection) {
			iziToast.error({
				message: 'Please select  section  before uploading.'
			})
			return
		}

		setLoading(true)

		try {
			const userId = localStorage.getItem('userId') || ''
			const formData = new FormData()

			// Get names from selected section/subsection
			const selectedSectionObj = sectionData.find(sec => sec.sectionId === selectedSection)
			const selectedSubSectionObj = subSectionData.find(
				sub => sub.subSectionId === selectedSubSection
			)

			formData.append('file', file)
			formData.append(
				'sectionName',
				isCustomSection ? customSection : selectedSectionObj?.sectionName || ''
			)
			formData.append('sectionId', selectedSection)
			formData.append(
				'subSectionName',
				isCustomSubSection ? customSubSection : selectedSubSectionObj?.subSectionName || ''
			)
			formData.append('subSectionId', selectedSubSection)
			formData.append('userId', userId)

			const response = await axios.post(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/uploadQuestions`,
				formData,
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			if (response.status === 200 || response.status === 201) {
				iziToast.success({ message: 'File uploaded successfully!' })
				setFile(null)
				setSelectedSection('')
				setSelectedSubSection('')
				setCustomSection('')
				setCustomSubSection('')
				handleClose()
				refreshData()
			} else {
				iziToast.error({ message: 'Unexpected response from the server' })
			}
		} catch (error) {
			iziToast.error({ message: 'File upload failed. Please try again.' })
			console.error('Upload error:', error)
		} finally {
			setLoading(false)
		}
	}

	const onDrop = useCallback((acceptedFiles: File[], fileRejections: FileRejection[]) => {
		if (acceptedFiles.length > 0) {
			setFile(acceptedFiles[0])
		}
		fileRejections.forEach(({ errors }) => {
			errors.forEach(({ code }) => {
				if (code === 'file-invalid-type') {
					iziToast.error({ message: 'File type must be CSV or XLSX.' })
				} else if (code === 'file-too-large') {
					iziToast.error({ message: 'File size must not be larger than 2MB.' })
				}
			})
		})
	}, [])

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: ['.csv', '.xlsx'],
		maxSize: 2000000,
		multiple: false
	})

	const { onClick: onBrowseFileClick, ...logoUploadContainerProps } = getRootProps()

	const fetchSections = async () => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/getSections`,
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			const sections = response.data.map((section: any) => ({
				sectionId: section._id,
				sectionName: section.name,
				subSections: []
			}))
			setSectionData(sections)
		} catch (error) {
			console.error('Error fetching sections:', error)
		}
	}

	useEffect(() => {
		fetchSections()
	}, [])

	const fetchSubSectionsWithSectionId = async (sectionId: string) => {
		try {
			const response = await axios.get(
				`${process.env.REACT_APP_API_URL}/api/v1/custom/getSubSectionsWithSectionId/${sectionId}`,
				{
					headers: {
						Authorization: ` ${token}`
					}
				}
			)

			const formattedSubSections = response.data.map(item => ({
				subSectionId: item._id,
				subSectionName: item.name,
				sectionName: item.sectionName
			}))

			setSubSectionData(formattedSubSections)
		} catch (error) {
			console.error('Error fetching subsections:', error)
		}
	}
	useEffect(() => {
		if (selectedSection) {
			fetchSubSectionsWithSectionId(selectedSection)
		}
	}, [selectedSection])
	const formattedSectionData = sectionData.map(section => ({
		id: section.sectionId,
		name: section.sectionName
	}))
	const handleCancel = () => {
		setFile(null)
		setSelectedSection('')
		setSelectedSubSection('')
		setCustomSection('')
		setCustomSubSection('')
		setIsCustomSection(false)
		setIsCustomSubSection(false)
		// handleClose()
	}

	return (
		<Box p={3}>
			<Typography variant="h4" gutterBottom>
				{intl.formatMessage({ id: 'Upload Question' })}
			</Typography>

			<Grid container spacing={3}>
				<Grid item xs={12}>
					<LogoUploadContainer {...logoUploadContainerProps} active={isDragActive}>
						<Grid
							container
							direction="column"
							justifyContent="center"
							alignItems="center"
							spacing={2}
						>
							{isDragActive ? (
								<Typography fontWeight={500}>
									{intl.formatMessage({ id: 'companySettings.dropYourFiles' })}!
								</Typography>
							) : (
								<>
									<Typography fontWeight={500}>
										{intl.formatMessage({ id: 'invite.challenge.uploadEmailFileDropZone' })}
									</Typography>
									<Typography fontWeight={500}>
										{intl.formatMessage({ id: 'or' }).toUpperCase()}
									</Typography>
									<Button
										type="button"
										variant="outlined"
										size="small"
										color="inherit"
										onClick={onBrowseFileClick}
									>
										{intl.formatMessage({ id: 'browseFiles' })}
									</Button>
								</>
							)}
						</Grid>
					</LogoUploadContainer>
					<input {...getInputProps()} />
				</Grid>

				<Grid item xs={12} container alignItems="center" justifyContent="space-between">
					<Typography sx={{ fontSize: '13px', color: '#f44336' }}>
						Allowed file formats: .csv, .xlsx
					</Typography>
					<Tooltip
						title={
							<Box>
								<Typography variant="body2">
									<b>Headers Format:</b>
								</Typography>
								<Typography variant="body2">
									No | questionType | question | answer1 | answer2 | answer3 | answer4
								</Typography>
								<Typography variant="body2">
									correctAnswer | noOfAnswers | explanation | marks | answerOption |
									negativeMarks (%)
								</Typography>
							</Box>
						}
						arrow
					>
						<Typography
							sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#1976d2' }}
						>
							Headers Format
						</Typography>
					</Tooltip>
				</Grid>

				{file && (
					<Grid item xs={12}>
						<Typography variant="body2">Selected file: {file.name}</Typography>
					</Grid>
				)}

				<Grid item xs={12} sm={6}>
					<Box>
						<Bulksections
							sectionData={sectionData}
							selectedSection={selectedSection}
							setSelectedSection={setSelectedSection}
							setSubSectionData={setSubSectionData}
							isCustomSection={isCustomSection}
							setIsCustomSection={setIsCustomSection}
							customSection={customSection}
							setCustomSection={setCustomSection}
							fetchSections={fetchSections}
						/>
					</Box>
				</Grid>

				<Grid item xs={12} sm={6}>
					<Box>
						<Bulksubsection
							sectionData={formattedSectionData}
							subSectionData={subSectionData}
							setSubSectionData={setSubSectionData}
							selectedSubSection={selectedSubSection}
							setSelectedSubSection={setSelectedSubSection}
							isCustomSubSection={isCustomSubSection}
							setIsCustomSubSection={setIsCustomSubSection}
							customSubSection={customSubSection}
							setCustomSubSection={setCustomSubSection}
							refetchSubSections={fetchSubSectionsWithSectionId}
						/>
					</Box>
				</Grid>

				<Grid item xs={12} container justifyContent="flex-end" spacing={2}>
					<Grid item>
						<Button
							variant="outlined"
							color="primary"
							onClick={handleCancel}
							disabled={loading}
						>
							Cancel
						</Button>
					</Grid>
					<Grid item>
						<Button
							variant="contained"
							color="primary"
							disabled={loading || !file || !selectedSection}
							onClick={handleUpload}
						>
							{loading ? 'Uploading...' : 'Submit'}
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	)
}

export default FileUpload
